import { defaultTo, equals, isEmpty, pathOr,toString } from 'ramda';
import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import store from 'src/store';
import { handleLogout } from 'src/store/authentication/authentication.actions';
import { AuthenticationHandler } from './services/auth/core_api';
import { reportException } from './exceptionReporting';
import { isNotEmpty } from './utilities/hasValue';
import { authenticatedUser } from './utilities/storage';

const handleSuccess = (response: AxiosResponse) => {
    if (!!response.headers['x-maintenance-mode']) {
        store.dispatch(handleLogout());
        Promise.reject(response);
    }
    return response;
};

const handleError = (error: AxiosError) => {
    const accountId = authenticatedUser.accountId.get()
    const statusCode = pathOr(0,['response','status'],error)
    if (equals(statusCode,401) && isNotEmpty(accountId)) {
        const email = pathOr('', ['response', 'config', 'headers', 'X-AUTH-LENDSMART-EMAIL'], error);
        const namespace = pathOr('', ['response', 'config', 'headers', 'X-AUTH-LENDSMART-NAMESPACE'], error);
        /**
         * this will blow out redux state and the componentDidUpdate in the
         * AuthenticationWrapper.tsx will be responsible for redirecting to Login
         */
        const errorResponse = pathOr({},['response','data'],error)
        const message = isNotEmpty(errorResponse) ? toString(errorResponse): ''
        reportException(`<---Error--Handler--401--${namespace}--${email}------${message}--->`)
        store.dispatch(handleLogout());
    }
    if (
        (error &&
            error.config &&
            error.config.headers &&
            !!error.config.headers['x-maintenance-mode']) ||
        /** The below code used to trap 403 and logged out automatically. This PR (https://github.com/lendsmartlabs/core_api/pull/502) in the core api, sends back
         *  the facade HTTPS status code of the Partner back to the UI. Eg: If house canary had auth failure
         *  of HTTP 403, we will redirect to login, which isnt desirable. Hence HTTP status code is stubbed to 1000 for now.
         *  Ideal fix is to send a message in the HTTP status code to tell us the calling system
         *  We need to redirect to login on 403 when our API sends those errors only.
         */
        (error && error.response && error.response.status === 1000)
    ) {
        store.dispatch(handleLogout());
    }

    // Just a hack when no data or the url doesn't exists.
    if (error.response && error.response.status === 404) {
        const email = pathOr('', ['response', 'config', 'headers', 'X-AUTH-LENDSMART-EMAIL'], error);
        const namespace = pathOr('', ['response', 'config', 'headers', 'X-AUTH-LENDSMART-NAMESPACE'], error);
        const errors: any = pathOr('', ['response', 'data'], error)
        if (isEmpty(errors)) {
            reportException(`<----Empty response-404--${namespace}--${email}--->`)
        }
        return Promise.resolve({});
    }
    return Promise.reject(error);
};

export const base64Headers = (
    accountId: any,
    accountEmail: any,
    token: any
): string => {
    const defaultToEmpty = defaultTo('');

    return window.btoa(
        JSON.stringify({
            email: defaultToEmpty(accountEmail),
            token: defaultToEmpty(token),
            org_id: '',
            team_id: '',
            account_id: defaultToEmpty(accountId).toString(),
        })
    );
};

const coreapi = new AuthenticationHandler()


Axios.interceptors.request.use(
    (config: AxiosRequestConfig): AxiosRequestConfig => {
        const authenticator = coreapi.getAuthenticator()
        return authenticator.applyAuthentication(config)
    }
);

/*
Interceptor that initiates re-authentication if:
  * The response is HTTP 401 "Unauthorized"
  * The API is in Maintainence mode
Also rejects non-error responses if the API is in Maintainence mode
*/
Axios.interceptors.response.use(handleSuccess, handleError);
